// Own system screen (spec §3.3): header with this system's stock, then tabs. Presentational: the tab,
// actions and `now` come in as props, so it renders in tests without a router state or a server.
import type { FullSystem, GalaxyMeResponse, Scout } from '@stars-and-ships/api-types';
import type { ReactNode } from 'react';
import { formatDuration, formatNumber, SHIP_LABELS } from '../../labels';
import { liveResources } from '../../state/live';
import { ErrorNotice, Panel, type TabItem, TabPanel, Tabs } from '../ui';
import { BuildingsTab, QueuePanel } from './BuildingsTab';
import { EmbassyTab, liveInfluence } from './EmbassyTab';
import { FirstGoalsPanel } from './FirstGoalsPanel';
import { PortTab } from './PortTab';
import { ShipyardTab } from './ShipyardTab';
import { SystemHeader } from './SystemHeader';
import { ClaimsNotice } from './shared';
import type { SystemActions, SystemTab } from './types';

const TABS: TabItem<SystemTab>[] = [
  { id: 'buildings', label: 'Buildings' },
  { id: 'shipyard', label: 'Shipyard' },
  { id: 'port', label: 'Port' },
  { id: 'embassy', label: 'Embassy' },
];

/** Desktop right pane: what the other tabs are doing, one tap away. */
function SummaryPanel({
  system,
  now,
  onTabChange,
}: {
  system: FullSystem;
  now: number;
  onTabChange(tab: SystemTab): void;
}) {
  const batch = system.shipProduction.find((i) => i.startedAt) ?? system.shipProduction[0];
  const trade = system.portTrade;
  const rows: { tab: SystemTab; label: string; value: string }[] = [
    {
      tab: 'shipyard',
      label: 'Shipyard',
      value: batch
        ? `${SHIP_LABELS[batch.shipType]} ${formatNumber(batch.delivered)}/${formatNumber(batch.quantity)}`
        : 'Line free',
    },
    {
      tab: 'port',
      label: 'Port',
      value:
        system.tradingPort.level === 0
          ? 'No port'
          : trade
            ? `Ready in ${formatDuration(Math.max(0, Date.parse(trade.completesAt) - now))}`
            : 'Idle',
    },
    {
      tab: 'embassy',
      label: 'Influence',
      value: `${formatNumber(liveInfluence(system, now))} / ${formatNumber(system.influence.cap)}`,
    },
  ];
  return (
    <Panel title="This system">
      <ul data-system-summary className="divide-y divide-line">
        {rows.map((r) => (
          <li key={r.tab}>
            <button
              type="button"
              onClick={() => onTabChange(r.tab)}
              className="flex min-h-11 w-full items-center justify-between gap-2 text-left hover:text-accent"
            >
              <span className="font-display text-[10px] font-bold uppercase tracking-[0.16em] text-faint">
                {r.label}
              </span>
              <span className="font-mono text-sm tabular-nums text-text">{r.value}</span>
            </button>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function OwnSystemView({
  galaxyId,
  system,
  now,
  tab,
  onTabChange,
  actions,
  scouts,
  alerts,
  firstGoals,
}: {
  galaxyId: string;
  system: FullSystem;
  now: number;
  tab: SystemTab;
  onTabChange(tab: SystemTab): void;
  actions?: SystemActions;
  scouts?: Scout[] | undefined;
  /** Battle and incoming-fleet alerts, shown under the header. */
  alerts?: ReactNode;
  /** Starter checklist, set only on the player's home system. */
  firstGoals?: GalaxyMeResponse['firstGoals'] | undefined;
}) {
  const have = liveResources(system.resources, now);
  return (
    <div className="space-y-4">
      <SystemHeader galaxyId={galaxyId} system={system} have={have} />
      {alerts}
      {actions?.error ? <ErrorNotice error={actions.error} /> : null}
      <ClaimsNotice system={system} now={now} />
      {firstGoals ? (
        <FirstGoalsPanel galaxyId={galaxyId} firstGoals={firstGoals} onTabChange={onTabChange} />
      ) : null}
      {/* One queue element: on phones it tops the Buildings tab, from lg it sits in the right pane. */}
      <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
        <div className="lg:col-start-1 lg:row-start-1">
          <Tabs label="System" tabs={TABS} value={tab} onChange={onTabChange} />
        </div>
        <aside
          className={`space-y-4 lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:block ${tab === 'buildings' ? '' : 'hidden'}`}
        >
          <QueuePanel queue={system.buildingQueue} now={now} actions={actions} />
          <div className="hidden lg:block">
            <SummaryPanel system={system} now={now} onTabChange={onTabChange} />
          </div>
        </aside>
        <div className="-mt-4 min-w-0 lg:col-start-1 lg:row-start-2 lg:mt-0">
          <TabPanel id={tab}>
            {tab === 'buildings' ? (
              <BuildingsTab galaxyId={galaxyId} system={system} have={have} actions={actions} />
            ) : tab === 'shipyard' ? (
              <ShipyardTab
                galaxyId={galaxyId}
                system={system}
                have={have}
                now={now}
                actions={actions}
                onTabChange={onTabChange}
              />
            ) : tab === 'port' ? (
              <PortTab system={system} have={have} now={now} actions={actions} />
            ) : (
              <EmbassyTab galaxyId={galaxyId} system={system} now={now} scouts={scouts} />
            )}
          </TabPanel>
        </div>
      </div>
    </div>
  );
}
